/**
 * useConcurrentData — Concurrent data fetching with SWR caching.
 *
 * Flow:
 *   1. Check IndexedDB cache → show stale result immediately
 *   2. Fire fetch in background → UI stays interactive
 *   3. When fresh data arrives → update cache + UI
 *   4. Show "Background Processing" indicator during fetch
 *
 * The user can navigate to other tabs while data loads.
 */

import { useState, useCallback, useRef } from 'react';
import { getCached, setCache } from '../store/cache_engine';

export default function useConcurrentData(clientId) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bgLoading, setBgLoading] = useState(false);
  const [cacheHit, setCacheHit] = useState(false);
  const [staleData, setStaleData] = useState(false);
  const abortRef = useRef(null);

  /**
   * Execute a query with SWR caching.
   * @param query - the question text
   * @param fetchFn - async function that returns the result
   * @param options - { forceRefresh: true } to bypass cache and always hit DB
   * @returns the result (cached or fresh)
   */
  const execute = useCallback(async (query, fetchFn, options = {}) => {
    const { forceRefresh = false } = options;

    // Step 1: Check cache immediately (skip if force refresh)
    const cached = !forceRefresh ? await getCached(clientId, query) : null;
    if (cached && cached.data) {
      setResult(cached.data);
      setCacheHit(true);
      setStaleData(cached.isStale);

      if (!cached.isStale) {
        // Fresh cache — no need to fetch
        return cached.data;
      }

      // Stale cache — show it but revalidate in background
      setBgLoading(true);
    } else {
      // No cache or force refresh — show loading
      setLoading(true);
      setCacheHit(false);
    }

    // Step 2: Fetch fresh data (background if we have stale cache)
    try {
      // Cancel any previous in-flight request
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const freshResult = await fetchFn(query, controller.signal);

      if (freshResult && !freshResult.error) {
        // Step 3: Update cache
        await setCache(clientId, query, freshResult);

        // Step 4: Update UI
        setResult(freshResult);
        setCacheHit(false);
        setStaleData(false);
      } else if (!cached) {
        // No cache and fetch failed — show error
        setResult(freshResult);
      }
    } catch (err) {
      if (err.name !== 'AbortError' && !cached) {
        setResult({ error: err.message });
      }
    } finally {
      setLoading(false);
      setBgLoading(false);
    }

    return result;
  }, [clientId, result]);

  const clear = useCallback(() => {
    setResult(null);
    setCacheHit(false);
    setStaleData(false);
  }, []);

  return {
    result,
    setResult,
    loading,
    bgLoading,      // true when revalidating stale cache in background
    cacheHit,        // true when showing cached data
    staleData,       // true when cached data is stale (revalidating)
    execute,
    clear,
  };
}
